import { Button } from "@/components/ui/button";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarTrigger,
} from "@/components/ui/menubar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { BOOKING_STATUS } from "@/constants/booking-status";
import { StatusBadge } from "@/features/private-room/bookings/layouts/shared/status-badge";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/utils/format/currency";
import { convertBookingTypeToName } from "@/utils/convert-booking-type-to-name";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { TrashIcon } from "@heroicons/react/24/outline";
import { SheetEditBooking } from "../shared/sheet-edit-booking";
import { BookingListTableProps } from "./booking-list-table.types";
import { useNavigate } from "react-router-dom";
import { formatInTimeZone } from "date-fns-tz";

export const BookingListTable = ({
  data,
  handleSelectItem,
  updateFilters,
  filters,
}: BookingListTableProps): JSX.Element => {
  const navigate = useNavigate();

  const allowEditOrCancel = (bookingDate: number) => {
    const today = new Date();
    const bookingDateObj = new Date(
      `${String(bookingDate).slice(0, 10)}T00:00:00`,
    );
    const bookingMonth = new Date(bookingDateObj).getMonth();
    const bookingYear = new Date(bookingDateObj).getFullYear();
    const currentMonth = new Date(today).getMonth();
    const currentYear = new Date(today).getFullYear();

    const isBookingMonthSameOrLater =
      (bookingYear === currentYear && bookingMonth >= currentMonth) ||
      bookingYear > currentYear;

    return isBookingMonthSameOrLater;
  };

  const formatBookingDetail = (bookingDetail: string) => {
    let [startDate, endDate] = bookingDetail.split(" - ");

    startDate = formatInTimeZone(startDate, "UTC", "dd-MM-yyyy");
    endDate = formatInTimeZone(endDate, "UTC", "dd-MM-yyyy");

    return (
      <div>
        {startDate} <br />
        {endDate}
      </div>
    );
  };

  return (
    <div className=" mx-auto my-8">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[24px]" />
            <TableHead>ID Booking</TableHead>
            <TableHead>ID Contrato</TableHead>
            <TableHead>Data de início / fim</TableHead>
            <TableHead>Empresa</TableHead>
            <TableHead>Espaço / Escritório</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Take</TableHead>
            <TableHead>Créditos</TableHead>
            <TableHead>Repasse R$</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data?.map((item) => (
            <TableRow key={item.id}>
              <TableCell></TableCell>
              <TableCell className="font-medium py-5">{item.id}</TableCell>
              <TableCell>{item.po_contract_id || "---"}</TableCell>
              <TableCell>{formatBookingDetail(item.booking_detail)}</TableCell>
              <TableCell>{item.companies.name}</TableCell>
              <TableCell>
                {item.spaces.name} - {item.privateRooms?.label}
              </TableCell>
              <TableCell>{convertBookingTypeToName(item.type)}</TableCell>
              <TableCell>
                {item.bookingAttributes?.booking_payout_rate_perc}%
              </TableCell>
              <TableCell>{item.credits}</TableCell>
              <TableCell>
                {formatCurrency(
                  Number(item.bookingAttributes.booking_payout) || 0,
                )}
              </TableCell>

              <TableCell>
                <StatusBadge status={item.status} type="STATUS_BOOKINGS" />
              </TableCell>

              <TableCell>
                <Menubar className="border-none shadow-none relative">
                  <MenubarMenu>
                    <MenubarTrigger className="cursor-pointer">
                      <EllipsisHorizontalIcon width={20} />
                    </MenubarTrigger>
                    <MenubarContent>
                      <MenubarItem onClick={() => handleSelectItem(item, true)}>
                        Visualizar
                      </MenubarItem>

                      {!!item.po_contract_id && (
                        <MenubarItem
                          onClick={() =>
                            navigate(
                              `/private-office/contracts/list?po_contract_id=${item.po_contract_id}`,
                            )
                          }
                        >
                          Ver contrato
                        </MenubarItem>
                      )}

                      {![
                        BOOKING_STATUS.CANCELLED,
                        BOOKING_STATUS.USER_CANCELLED,
                      ].includes(item.status) &&
                        allowEditOrCancel(item.booking_date) && (
                          <>
                            <SheetEditBooking
                              data={item}
                              updateFilters={updateFilters}
                              filters={filters}
                            >
                              <Button
                                variant="ghost"
                                className={cn(
                                  "relative flex cursor-default w-full select-none items-center justify-start rounded-sm px-2 py-0 h-8 text-sm font-normal outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                                )}
                              >
                                <span>Editar</span>
                              </Button>
                            </SheetEditBooking>

                            {import.meta.env.VITE_PO_CONTRACTS_RELEASED ==
                              "false" && ( //@TODO: remove cancel button after contracts released
                              <>
                                <MenubarSeparator />
                                <MenubarItem
                                  className="text-red-600"
                                  onClick={() =>
                                    navigate(
                                      `/private-office/bookings/downsell/${item.id}`,
                                    )
                                  }
                                >
                                  <TrashIcon
                                    className="text-red-600 hover:text-red-400   mr-2"
                                    width={16}
                                  />
                                  Cancelar
                                </MenubarItem>
                              </>
                            )}
                          </>
                        )}
                    </MenubarContent>
                  </MenubarMenu>
                </Menubar>
              </TableCell>
            </TableRow>
          ))}
          {data?.length === 0 && (
            <TableRow>
              <TableCell colSpan={12}>
                <p className="font-bold mt-5 text-center text-lg">
                  Não foi encontrado nenhum booking.
                </p>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
